// Imported by file rather than through the folder barrel: this module is a
// value dependency of `PipelineStepCategory`, and the projector's own import of
// `../../types` is type-only to keep that one-way. Naming the file confines
// that invariant to a single pair rather than to everything the barrel exports.
import { LabelNameProjector } from '../services/label-name-projector/label-name-projector.service';

export type { GenerateFindingValue } from '@telespot/domain';

export type Language = 'en' | 'es' | 'fr' | 'pt' | 'it';

export type LocalizedName = Partial<Record<Language, string>> & { en: string };

/**
 * The minimum a label reference carries to be judged properly defined: the uuid
 * a step's params store, and the name the `Label` table is meant to supply for
 * it. Named here so `LabelNameProjector.isProperlyDefined` has one contract to
 * state, and so the category editor's own option type can satisfy it too.
 */
export interface LabelReference {
  uuid: string;
  name: LocalizedName;
}

export interface PipelineStepCategoryOption extends LabelReference {
  thrdis: number;
}

export class PipelineStepCategory implements LabelReference {
  public uuid: string;
  public name: LocalizedName;
  public options: PipelineStepCategoryOption[];

  constructor(data: {
    uuid: string;
    name: LocalizedName;
    options: PipelineStepCategoryOption[];
  }) {
    this.uuid = data.uuid;
    this.name = data.name;
    this.options = data.options;
  }

  /**
   * Every name this category carries — its own and its options' — keyed by
   * uuid, in the shape `withResolvedNames` consumes.
   */
  public namesByUuid(): Record<string, LocalizedName> {
    return Object.fromEntries(this.labelReferences().map((reference) => [reference.uuid, reference.name]));
  }

  /**
   * Every label uuid this category references — its own and its options' — in
   * declaration order. What a bulk name lookup has to ask for; the traversal
   * lives here so callers do not walk `options` themselves.
   */
  public labelUuids(): string[] {
    return this.labelReferences().map((reference) => reference.uuid);
  }

  /**
   * A copy carrying the names it was given, in place of the uuid placeholders
   * `PipelineStepDraftProjector.toStructuredView` hands out. A uuid with no
   * entry keeps the name it already has, so it stays visible and correctable
   * rather than disappearing.
   */
  public withResolvedNames(namesByUuid: Readonly<Record<string, LocalizedName>>): PipelineStepCategory {
    return new PipelineStepCategory({
      uuid: this.uuid,
      name: namesByUuid[this.uuid] ?? this.name,
      options: this.options.map((option) => ({
        ...option,
        name: namesByUuid[option.uuid] ?? option.name,
      })),
    });
  }

  /**
   * The uuids among this category and its options whose labels are not properly
   * defined. A thin traversal over `LabelNameProjector.isProperlyDefined`, so
   * the rule itself is stated once and not restated here.
   */
  public undefinedLabelUuids(): string[] {
    return this.labelReferences()
      .filter((reference) => !LabelNameProjector.isProperlyDefined(reference))
      .map((reference) => reference.uuid);
  }

  /** This category and its options, as the label references they both are. */
  private labelReferences(): LabelReference[] {
    return [this, ...this.options];
  }

  public clone(): PipelineStepCategory {
    return new PipelineStepCategory({
      uuid: this.uuid,
      name: { ...this.name },
      options: this.options.map((opt) => ({
        uuid: opt.uuid,
        name: { ...opt.name },
        thrdis: opt.thrdis,
      })),
    });
  }
}
